// PaymentApprovalModal.js - Updated with Better UI for Date Selection
import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  Button,
  Input,
  Label,
  Badge,
} from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const initialFields = {
  remark: { value: "", error: "" },
  paymentDate: { value: null, error: "" },
};

/**
 * PaymentApprovalModal
 *
 * props:
 * - isOpen: boolean
 * - toggleModal: () => void
 * - payment: { 
 *     id, payerName, courseName, batchName, totalAmount, 
 *     totalGst, payableAmount, paidAmount, paymentMode, 
 *     paymentEntryDate, paymentDate, enrolledTraineesCount 
 *   }
 * - onApprove: (remark: string, paymentDate: Date) => void
 * - onReject: (remark: string) => void
 * - loaderOnClick: "approve" | "reject" | null
 */
const PaymentApprovalModal = ({
  isOpen,
  toggleModal,
  payment,
  onApprove,
  onReject,
  loaderOnClick,
}) => {
  const [fields, setFields] = useState(initialFields);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFields(initialFields);
    }
  }, [isOpen]);

  const handleChange = (name, value) => {
    setFields((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
        error: "",
      },
    }));
  };

  const getValidationError = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;

    switch (name) {
      case "remark":
        return !trimmed ? "Please enter a remark" : "";
      case "paymentDate":
        return !value ? "Please select a payment date" : "";
      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    setFields((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: typeof value === "string" ? value.trim() : value,
        error: getValidationError(name, value),
      },
    }));
  };

  const validateForm = () => {
    let updated = { ...fields };

    Object.keys(updated).forEach((key) => {
      updated[key] = {
        ...updated[key],
        error: getValidationError(key, updated[key].value),
      };
    });

    setFields(updated);

    return Object.values(updated).every((item) => item.error === "");
  };

  const handleApprove = () => {
    if (!validateForm()) return;
    onApprove(fields.remark.value, fields.paymentDate.value);
  };

  const handleReject = () => {
    if (!validateForm()) return;
    onReject(fields.remark.value);
  };

  // If no payment data, return null
  if (!payment) return null;

  // Extract payment data with proper fallbacks
  const {
    payerName = "-",
    courseName = "-",
    batchName = "-",
    totalAmount = 0,
    totalGst = 0,
    payableAmount = 0,
    paidAmount = 0,
    paymentMode = "-",
    paymentEntryDate = "-",
    // paymentDate = "-",
    enrolledTraineesCount = 0,
  } = payment;

  // Calculate GST percentage if totalAmount and totalGst are available
  const gstPercentage = totalAmount > 0 && totalGst > 0 
    ? Math.round((totalGst / totalAmount) * 100) 
    : 18;

  const isPaid = paidAmount > 0;

  // Get today's date for max date
  const today = new Date();

  return (
    <>
      {isOpen && <div className="blur-overlay"></div>}

      <Modal
        isOpen={isOpen}
        toggle={toggleModal}
        backdrop={false}
        keyboard={false}
        className="modal-dialog-centered payment-approval-modal"
        size="md"
      >
        <ModalBody className="text-center px-4 pt-4 pb-3">
          {/* CLOSE */}
          <button
            type="button"
            className="close position-absolute payment-close-button"            
            onClick={toggleModal}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>

          {/* ICON */}
          <div className="payment-modal-icon mx-auto mb-2">
            <i className="fas fa-receipt" />
          </div>

          <h4 className="mb-1">Review payment request</h4>
          <p className="text-muted mb-3 payment-modal-small-text">
            Verify the details before you approve or reject
          </p>

          {/* TOTAL + MODE */}
          <div className="mb-3">
            <h2 className="mb-1">
              &#8377;
              {payableAmount.toLocaleString("en-IN", { 
                minimumFractionDigits: 2, 
                maximumFractionDigits: 2 
              })}
            </h2>
            <div className="d-flex justify-content-center align-items-center gap-2">
              <Badge
                color={paymentMode === "ONLINE" ? "success" : "warning"}
                pill
                className="px-3 py-1"
              >
                <i className={`fas fa-${paymentMode === "ONLINE" ? "wifi" : "building"} mr-1`} />
                {paymentMode}
              </Badge>
              {isPaid ? (
                <Badge color="success" pill className="px-3 py-1">
                  <i className="fas fa-check-circle mr-1" />
                  Paid
                </Badge>
              ) : (
                <Badge color="warning" pill className="px-3 py-1">
                  <i className="fas fa-clock mr-1" />
                  Pending
                </Badge>
              )}
            </div>
          </div>

          {/* DETAIL ROWS */}
          <div className="payment-detail-list text-left border-top pt-3 mb-3">
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted payment-modal-header">Name</span>
              <span className="payment-modal-values">{payerName}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted payment-modal-header">Course</span>
              <span className="payment-modal-values">{courseName}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted payment-modal-header">Batch</span>
              <span className="payment-modal-values">{batchName}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted payment-modal-header">Base Amount</span>
              <span className="payment-modal-values">
                &#8377;{totalAmount.toLocaleString("en-IN", { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted payment-modal-header">
                Tax ({gstPercentage}% GST)
              </span>
              <span className="payment-modal-values">
                &#8377;{totalGst.toLocaleString("en-IN", { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </span>
            </div>
            <div className="d-flex justify-content-between mb-2 payment-modal-payable-amount">
              <span className="text-muted font-weight-bold payment-modal-values">
                Payable Amount
              </span>
              <span className="payment-amount-text">
                &#8377;{payableAmount.toLocaleString("en-IN", { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted payment-modal-header">
                Paid Amount
              </span>
              <span className="payment-amount-text">
                &#8377;{paidAmount.toLocaleString("en-IN", { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })}
              </span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted payment-modal-header">
                Enrolled Trainees
              </span>
              <span className="payment-modal-values">
                <i className="fas fa-users mr-1" />
                {enrolledTraineesCount}
              </span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted payment-modal-header">
                Requested on
              </span>
              <span className="payment-modal-values">
                {paymentEntryDate}
              </span>
            </div>
          </div>

          {/* PAYMENT DATE - Improved UI */}
          <div className="text-left mb-3">
            <Label className="font-weight-bold payment-modal-small-text">
              <i className="fas fa-calendar-alt mr-1" />
              Payment Date <span className="text-danger">*</span>
            </Label>
            <div className="payment-date-container">
              <DatePicker
                onChangeRaw={(e) => e.preventDefault()}
                selected={fields.paymentDate.value}
                onChange={(date) => handleChange("paymentDate", date)}
                onBlur={() => handleOnBlur("paymentDate", fields.paymentDate.value)}
                placeholderText="Select payment date"
                className={`form-control payment-date-input ${fields.paymentDate.error ? "is-invalid" : ""}`}
                dateFormat="dd-MMM-yyyy"
                showMonthDropdown
                showYearDropdown
                scrollableYearDropdown
                calendarClassName="custom-calendar"
                yearDropdownItemNumber={100}
                maxDate={today}
                minDate={new Date(2020, 0, 1)}
                isClearable
                required
                popperPlacement="bottom-start"
              />
              {fields.paymentDate.value && (
                <div className="payment-date-preview mt-1">
                  <Badge color="info" className="px-3 py-2">
                    <i className="fas fa-check-circle mr-1" />
                    Selected: {fields.paymentDate.value.toLocaleDateString('en-IN', { 
                      day: '2-digit', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </Badge>
                </div>
              )}
              {fields.paymentDate.error && (
                <div className="invalid-feedback d-block">
                  {fields.paymentDate.error}
                </div>
              )}
            </div>
          </div>

          {/* REMARK */}
          <div className="text-left mb-3">
            <Label className="font-weight-bold payment-modal-small-text">
              <i className="fas fa-comment mr-1" />
              Remark <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows="2"
              placeholder="Add a remark for this decision"
              value={fields.remark.value}
              onChange={(e) => handleChange("remark", e.target.value)}
              onBlur={(e) => handleOnBlur("remark", e.target.value)}
              maxLength={300}
              className={`${fields.remark.error ? "is-invalid" : ""} payment-modal-remark-textarea`}
            />
            <div className="d-flex justify-content-between mt-1">
              {fields.remark.error ? (
                <div className="invalid-feedback d-block">
                  {fields.remark.error}
                </div>
              ) : (
                <small className="text-muted">
                  {fields.remark.value.length}/300 characters
                </small>
              )}
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div className="d-flex gap-2 mt-3">
            <Button
              outline
              color="danger"
              onClick={handleReject}
              className="flex-fill payment-modal-approve-reject-btn"
              disabled={loaderOnClick === "approve"}
            >
              {loaderOnClick === "reject" ? (
                <i className="fa fa-spinner fa-spin" />
              ) : (
                <>
                  <i className="fas fa-times mr-1" />
                  Reject
                </>
              )}
            </Button>

            <Button
              color="success"
              onClick={handleApprove}
              className="flex-fill payment-modal-approve-reject-btn"
              disabled={loaderOnClick === "reject"}
            >
              {loaderOnClick === "approve" ? (
                <i className="fa fa-spinner fa-spin text-white" />
              ) : (
                <>
                  <i className="fas fa-check mr-1" />
                  Approve
                </>
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      
    </>
  );
};

export default PaymentApprovalModal;